import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeImageFile } from '../../lib/photo';
import { haptic } from '../../lib/haptic';

type Props = {
  value?: string | null;
  onChange: (dataUrl: string | null) => void;
  /** Defaults to image+PDF for ID docs. Pass `image/*` for photo-only contexts. */
  accept?: string;
  label?: string;
};

const MAX_URL_LEN = 10_000_000; // 10 MB base64 ≈ 7.5 MB raw

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });

const isPdf = (dataUrl: string) => dataUrl.startsWith('data:application/pdf');

/**
 * A single unified document picker: one dashed tile that opens the platform
 * sheet (Camera / Photo Library / Files) and accepts an image OR a PDF.
 *
 * Deliberately omits the `capture` attribute so the OS offers every source
 * rather than forcing the camera. This replaced an earlier two-control layout
 * — a camera-only dropzone next to a separate "Choose file" button — which was
 * redundant (both pick a document) and visually mismatched (different heights,
 * borders, and only one carried a label, so they never aligned).
 */
export function DocPicker({ value, onChange, accept = 'image/*,application/pdf', label }: Props) {
  const { t } = useTranslation('profile');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onPickFile = async (file: File) => {
    setBusy(true);
    setError(null);
    try {
      if (file.type.startsWith('image/')) {
        const dataUrl = await resizeImageFile(file);
        haptic.select();
        onChange(dataUrl);
        return;
      }
      if (file.type === 'application/pdf') {
        const dataUrl = await readAsDataUrl(file);
        if (dataUrl.length > MAX_URL_LEN) {
          setError(t('kycDocs.docTooLarge'));
          return;
        }
        haptic.select();
        onChange(dataUrl);
        return;
      }
      setError(t('kycDocs.docUnsupported'));
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : null;
      setError(msg ?? t('kycDocs.viewerError'));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => { haptic.tap(); onChange(null); };

  return (
    <div className="space-y-2">
      {value ? (
        isPdf(value) ? (
          // PDF can't render in an <img>; show a labelled chip with a clear button.
          <div className="flex items-center gap-3 w-full px-4 py-4 rounded-2xl border border-[var(--color-border,#d1d5db)] bg-[var(--color-surface-soft)]">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span className="text-sm font-medium text-[var(--color-text)]">{t('kycDocs.pdfOnFile')}</span>
            <button
              type="button"
              onClick={clear}
              className="ml-auto text-xs font-semibold text-[var(--color-text-secondary)] px-3 py-1 rounded-full border border-[var(--color-border,#d1d5db)]"
            >
              {t('clear', { ns: 'common' })}
            </button>
          </div>
        ) : (
          <div className="relative">
            <img src={value} alt={t('kycDocs.photoTitle')} className="w-full rounded-2xl border border-[var(--color-border,#d1d5db)]" />
            <button
              type="button"
              onClick={clear}
              className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1 rounded-full"
            >
              {t('clear', { ns: 'common' })}
            </button>
          </div>
        )
      ) : (
        <button
          type="button"
          onClick={() => { haptic.tap(); fileInputRef.current?.click(); }}
          disabled={busy}
          className="w-full py-6 flex flex-col items-center gap-2 border-2 border-dashed border-[var(--color-border,#d1d5db)] rounded-2xl text-sm text-[var(--color-text-secondary)] disabled:opacity-50"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
            <circle cx="12" cy="13" r="3.5" />
          </svg>
          {busy ? t('photo_capture_processing', { ns: 'common' }) : t('kycDocs.uploadPrompt')}
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onPickFile(f);
          // Reset so picking the same file twice still fires onChange
          e.target.value = '';
        }}
      />
      {error ? <p className="text-sm text-[var(--color-danger,#C13834)]" role="alert">{error}</p> : null}
      {label ? <p className="text-xs text-[var(--color-text-secondary)]">{label}</p> : null}
    </div>
  );
}
